import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoaded(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const stars: Array<{ x: number; y: number; size: number; speed: number; opacity: number }> = [];
    for (let i = 0; i < 150; i++) {
      stars.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, size: Math.random() * 2, speed: Math.random() * 0.3 + 0.1, opacity: Math.random() * 0.5 + 0.3 });
    }
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        star.y += star.speed;
        if (star.y > canvas.height) { star.y = 0; star.x = Math.random() * canvas.width; }
      });
      requestAnimationFrame(animate);
    }
    animate();
    const handleResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-black">
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 z-10"></div>

      <div className={`relative z-20 flex flex-col items-center px-4 w-full transition-all duration-2000 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        <div
          ref={logoRef}
          className={`relative mb-8 md:mb-12 transition-all duration-2000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ animation: isLoaded ? 'float 6s ease-in-out infinite, glow 3s ease-in-out infinite' : 'none' }}
        >
          <div className="absolute inset-0 bg-gradient-radial from-[#00d4ff]/30 via-[#00d4ff]/10 to-transparent blur-3xl scale-150"></div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/d6ebd2a1-416d-4d41-8f5c-6f36fe395e24.png"
              alt="Motion Boyz Logo"
              className="w-[200px] sm:w-[320px] md:w-[420px] lg:w-[500px] h-auto relative z-10"
              style={{ filter: 'drop-shadow(0 0 30px rgba(0, 212, 255, 0.6)) drop-shadow(0 0 60px rgba(0, 212, 255, 0.3))', animation: 'rotate3d 20s linear infinite' }}
            />
          </div>
        </div>

        <div className={`text-center mb-8 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-white text-lg sm:text-xl md:text-2xl font-light tracking-[0.3em] mb-4 uppercase">Motion Boyz</h1>
          <p className="text-[#00d4ff] text-sm sm:text-base font-light italic tracking-wider"
            style={{ textShadow: '0 0 20px rgba(0, 212, 255, 0.8), 0 0 40px rgba(0, 212, 255, 0.4)', animation: 'pulse 3s ease-in-out infinite' }}
          >
            Motion Never Stops
          </p>
        </div>

        <div className={`flex flex-col sm:flex-row gap-4 sm:gap-8 w-full sm:w-auto px-6 sm:px-0 transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="#collection"
            className="group relative px-8 py-3 sm:px-10 sm:py-4 border-2 border-white/80 rounded-full text-white font-light text-sm sm:text-base tracking-wider whitespace-nowrap overflow-hidden transition-all duration-500 hover:border-white hover:scale-105 cursor-pointer text-center"
          >
            <span className="relative z-10 transition-colors duration-500 group-hover:text-black">Shop Collection</span>
            <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </a>
          <a
            href="#universe"
            className="group relative px-8 py-3 sm:px-10 sm:py-4 bg-[#00d4ff] rounded-full text-white font-light text-sm sm:text-base tracking-wider whitespace-nowrap flex items-center justify-center gap-3 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] cursor-pointer"
          >
            <span>Explore The Motion</span>
            <i className="ri-arrow-right-line text-xl transition-transform duration-500 group-hover:translate-x-2"></i>
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <i className="ri-arrow-down-line text-white/60 text-2xl sm:text-3xl"></i>
      </div>

      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0px) rotateZ(0deg); } 50% { transform: translateY(-20px) rotateZ(2deg); } }
        @keyframes glow { 0%, 100% { filter: drop-shadow(0 0 30px rgba(0, 212, 255, 0.6)) drop-shadow(0 0 60px rgba(0, 212, 255, 0.3)); } 50% { filter: drop-shadow(0 0 50px rgba(0, 212, 255, 0.9)) drop-shadow(0 0 100px rgba(0, 212, 255, 0.5)); } }
        @keyframes rotate3d { 0% { transform: perspective(1000px) rotateY(0deg); } 100% { transform: perspective(1000px) rotateY(360deg); } }
        @keyframes shimmer { 0%, 100% { opacity: 0.2; transform: translateX(-100%); } 50% { opacity: 0.6; transform: translateX(100%); } }
        @keyframes pulse { 0%, 100% { opacity: 1; text-shadow: 0 0 20px rgba(0, 212, 255, 0.8), 0 0 40px rgba(0, 212, 255, 0.4); } 50% { opacity: 0.8; text-shadow: 0 0 30px rgba(0, 212, 255, 1), 0 0 60px rgba(0, 212, 255, 0.6); } }
      `}</style>
    </section>
  );
}
